import { useMoveRecovery } from "@/hooks/use_move_recovery";
import { useOnline } from "@/hooks/use_online";
import { useFamilyData } from "@/hooks/use_family_data";
import { useLedger } from "@/hooks/use_ledger";
import { FamilyHomeLine } from "./family_home_line";
/**
 * Horizon — the home screen (DESIGN_SYSTEM §4.1).
 *
 * The top 38.2% is the hero: what is left this period, a per-day figure, and
 * the one comparison line. Below it, the tide pools — each budgeted category
 * filling to its mark, and a wider pool for everything together. The List chip
 * (or a drag up on the pools) opens every category with its figures.
 *
 * Without any budget there is nothing for a pool to fill to, so the screen is
 * the spending and one plain nudge.
 *
 * When a period closes, the next open shows the tide report and the open after
 * that the swell — never both on one open (`period_end.ts`).
 *
 * D-4 — the water is decoration. Every figure here is readable with every pool
 * and canvas removed, and over-budget is a sign and a word, not only colour.
 */

import { usePullSync } from "@/hooks/use_pull_sync";
import { useHeroCount } from "@/hooks/use_hero_count";
import { cents } from "@/domain/money";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { SalarySplit } from "@/features/horizon/salary_split";
import { absCents, formatMoney } from "@/domain/money";
import { periodLengthDays } from "@/domain/period";
import { pendingRecurring } from "@/domain/recurring";
import { dailyAllowance } from "@/domain/totals";
import { Label, cx } from "@/features/common/primitives";
import { ListSheet } from "@/features/horizon/list_sheet";
import { type PeriodEndShow, periodEndMoment, periodName, tideReport } from "@/domain/period_end";
import { Pools } from "@/features/horizon/pools";
import { TideReportView } from "@/features/horizon/tide_report_view";
import { poolOrder, unbudgetedLine } from "@/features/horizon/tide_pools";
import {
  usePeriodEnd,
  usePeriodView,
  useProfile,
  useRecurringRules,
  useSyncStatus,
} from "@/hooks/use_data";
import { useAuth } from "@/hooks/use_auth";
import { hasSeen, markSeen } from "@/lib/period_end_seen";


export function HorizonView({ periodEndMoments = true }: { periodEndMoments?: boolean }): React.JSX.Element {
  const navigate = useNavigate();
  const online = useOnline();
  const ledger = useLedger();
  const familyData = useFamilyData();
  const familyScope = ledger?.kind === "family";
  const view = usePeriodView();
  const periodEnd = usePeriodEnd();
  const pull = usePullSync(periodEndMoments);
  const heroFinal = view.remaining === null ? view.spent : absCents(view.remaining);
  const heroCount = useHeroCount(heroFinal, periodEnd.loaded, periodEndMoments);
  const profile = useProfile();
  const sync = useSyncStatus();
  const { hasMoveFailures, dismissMoveFailures } = useMoveRecovery(sync);
  const { state: authState, requestReauth } = useAuth();
  const rules = useRecurringRules();

  const [listOpen, setListOpen] = useState(false);
  const [moment, setMoment] = useState<PeriodEndShow>(null);
  const [momentReady, setMomentReady] = useState(false);
  const decided = useRef(false);

  const rippleKey = view.standings.reduce((sum, s) => sum + s.spent, 0);
  const closedKey = periodEnd.closed.period.start.toISOString();

  // The period-end moment is decided once per open, as soon as the ledger has
  // loaded — so dismissing the report cannot bring the swell up behind it on
  // the same visit. The swell is marked seen the moment it is chosen: it is a
  // line, not a dialog, and "once, then still" means this open is its once.
  useEffect(() => {
    if (!periodEndMoments || decided.current || !periodEnd.loaded) return;
    decided.current = true;
    const chosen = periodEndMoment(periodEnd.closed, {
      report: hasSeen("report", closedKey),
      swell: hasSeen("swell", closedKey),
    });
    if (chosen === "swell") markSeen("swell", closedKey);
    setMoment(chosen);
    setMomentReady(true);
  }, [periodEndMoments, periodEnd.loaded, periodEnd.closed, closedKey]);

  const openCategory = (id: string): void => { if (familyScope) setListOpen(true); else void navigate(`/ledger?cats=${id}`); };
  const dueCount = useMemo(
    () => pendingRecurring(rules, new Date(), profile.timezone).length,
    [rules, profile.timezone],
  );

  const { pools, unbudgeted } = useMemo(() => poolOrder(view.standings), [view.standings]);
  const overCount = pools.filter((p) => p.isOver).length;
  const day = periodLengthDays(view.period) - view.daysLeft + 1;

  const remainingLabel =
    view.remaining === null
      ? formatMoney(view.spent, view.currency)
      : formatMoney(absCents(view.remaining), view.currency);
  const perDay =
    view.remaining !== null && view.remaining > 0 ? dailyAllowance(view.remaining, view.daysLeft) : null;

  const footer =
    dueCount > 0 ? (
      <>
        {dueCount === 1 ? "A repeating expense is due" : `${String(dueCount)} repeating expenses are due`} ·{" "}
        <button type="button" onClick={() => setListOpen(true)} className="min-h-[44px] font-semibold text-acc">
          see List
        </button>
      </>
    ) : overCount > 0 ? (
      `${String(overCount)} ${overCount === 1 ? "category" : "categories"} over ${overCount === 1 ? "its" : "their"} mark`
    ) : (
      [unbudgetedLine(unbudgeted, view.currency), familyScope ? "tap a pool for its figures" : "tap a pool for its ledger"].filter(Boolean).join(" · ")
    );

  return (
    <div ref={pull.host} {...pull.handlers} data-period-end={momentReady ? moment ?? "none" : "loading"} className="relative isolate flex min-h-dvh flex-col bg-bg pt-safe pb-safe-route">

      {familyData?.familyError ? <p role="alert" className="px-phi-1 text-phi-n1 text-over">{familyData.familyError}</p> : null}
      {pull.message ? <p key={pull.message} role="status" className={cx("px-phi-1 py-phi-n2 text-phi-n1", pull.tone === "success" && "feel-success", pull.tone === "error" && "feel-error text-over")}>{pull.message}</p> : null}
      {familyScope ? <Link to="/home" className="min-h-[44px] px-phi-1 content-center text-phi-n1 text-acc">‹ Home · 👪 {familyData?.family?.name ?? "Family"}</Link> : null}

      <div className="flex min-h-read shrink-0 flex-col">
      <div className="flex items-center justify-between gap-phi-n2 px-phi-1 py-phi-n2">
        <Label>{view.label}</Label>
        {view.hasAnyBudget ? <Label>Day {day}</Label> : null}
      </div>

      {/* ── The hero: the top 38.2% ── */}
      <section
        aria-label="This period"
        className="@container flex w-full flex-1 flex-col items-center justify-center gap-phi-n3 px-phi-1 pb-phi-0 text-center"
      >
        <Label>{!view.hasAnyBudget ? "Spent this period" : view.isOver ? "Below the surface" : "Still above water"}</Label>

        {/* Capped by its own column (container units), so a six-figure sum at
            320px and a large text size shrinks rather than being cut. `w-full`
            on the container is load-bearing: a size container has no intrinsic
            width and would collapse the figure to nothing. */}
        <output
          aria-label={view.isOver ? `−${remainingLabel}` : remainingLabel}
          aria-live="off"
          className={cx(
            "fig text-[length:min(var(--text-phi-6),17cqi)] leading-tight font-light tracking-[-0.03em]",
            view.isOver && "text-over",
          )}
        >
          {view.isOver ? `−${formatMoney(cents(heroCount), view.currency)}` : formatMoney(cents(heroCount), view.currency)}
        </output>

        <p className="text-phi-0 text-tx-2">
          {!view.hasAnyBudget ? (
            <>No budgets set yet · {view.daysLeft} days left</>
          ) : view.isOver ? (
            <>over budget overall · {view.daysLeft} days left</>
          ) : perDay ? (
            <>
              <span className="fig">{formatMoney(perDay, view.currency)}</span> a day · {view.daysLeft} days left
            </>
          ) : (
            <>
              <span className="fig">{formatMoney(view.spent, view.currency)}</span> spent · {view.daysLeft} days
              left
            </>
          )}
        </p>

        {/* The one comparison line (SPEC.md §8). Never more than one. */}
        {moment !== "swell" && view.comparison.hasBasis ? (
          <p className="text-phi-0 text-tx-2">
            <b className="fig text-tx">
              {formatMoney(absCents(view.comparison.deltaCents), view.currency)}
            </b>{" "}
            {view.comparison.deltaCents >= 0 ? "ahead of" : "behind"} last period at this point
          </p>
        ) : null}

        {!familyScope && view.savingsStandings.length > 0 && view.ceiling !== null ? (
          <SalarySplit saved={view.savingsCommitted} ceiling={view.ceiling} currency={view.currency} />
        ) : null}
        {!familyScope && view.allocationGap !== null && view.allocationGap !== 0 ? (
          <p className={cx("max-w-phi-12 pt-phi-n3 text-phi-n1", view.allocationGap < 0 ? "border-l-2 border-over pl-phi-n1 text-tx-2" : "text-tx-2")}>
            {view.allocationGap < 0 ? <>
              Your budgets come to <span className="tnum">{formatMoney(absCents(view.allocationGap), view.currency)}</span> more than you have to spend.
            </> : <><span className="tnum">{formatMoney(view.allocationGap, view.currency)}</span> is not in any category budget yet.</>}
          </p>
        ) : null}
        {!familyScope && view.ceiling !== null && view.ceiling < 0 ? <p className="max-w-phi-12 pt-phi-n3 text-phi-n1 text-over">Savings alone come to more than your salary.</p> : null}

        {moment === "swell" ? (
          <p role="status" className="pt-phi-n4 text-phi-1 font-bold text-balance">
            {periodName(periodEnd.closed.label)} ended above water.
          </p>
        ) : null}

        {authState === "session-expired" ? <button type="button" onClick={requestReauth} className="min-h-11 text-phi-n1 text-acc">Sign in again to sync</button> : null}
        {/* Any sync message, not only the `error` state (INV-7). */}
        {sync.error ? (
          <p role="alert" className="max-w-phi-12 pt-phi-n4 text-phi-n1 text-over">
            {sync.error}
            {hasMoveFailures ? <button type="button" className="block min-h-[44px] text-acc" onClick={() => { void dismissMoveFailures(); }}>Dismiss unfinished moves</button> : null}
          </p>
        ) : null}
        {!online && ((sync.pending ?? 0) + (familyData?.pending ?? 0)) > 0 ? (
          <p className="pt-phi-n4 text-phi-n1 text-tx-2">Offline · {familyData?.pending && sync.pending ? `${familyData.pending} family changes and ${sync.pending} of yours waiting to upload` : `${(sync.pending ?? 0) + (familyData?.pending ?? 0)} saved here, waiting to upload`}</p>
        ) : null}
      </section>
      {!familyScope && familyData?.family && familyData.view ? <FamilyHomeLine name={familyData.family.name} view={familyData.view} members={familyData.family.members.filter(member => member.left_at === null).length} /> : null}
      </div>

      {view.hasAnyBudget ? (
        <Pools
          pools={pools}
          overall={{
            ratio: view.ratio,
            remaining: view.remaining ?? view.spent,
            isOver: view.isOver,
          }}
          currency={view.currency}
          rippleKey={rippleKey + (moment === "swell" ? 1 : 0)}
          footer={footer}
          onOpenList={() => setListOpen(true)}
          onOpenCategory={openCategory}
        />
      ) : (
        <div className="mt-auto px-phi-1 pb-phi-1">
          {dueCount > 0 ? <p className="pb-phi-n2 text-phi-n1 text-tx-2">{footer}</p> : null}
          <div className="flex flex-wrap items-center justify-between gap-phi-n2">
            <p className="min-w-0 flex-1 border-l-2 border-acc pl-phi-n1 text-phi-n1 text-tx">
              Set a budget per category in{" "}
              <Link to={familyScope ? "/settings/family/budget" : "/settings/budget"} className="font-semibold text-acc underline underline-offset-2">
                Settings
              </Link>{" "}
              and the water starts to mean something.
            </p>
            <button
              type="button"
              onClick={() => setListOpen(true)}
              aria-haspopup="dialog"
              className="min-h-[44px] min-w-[44px] shrink-0 rounded-full border border-line bg-bg px-phi-0 text-phi-n1 font-semibold text-tx"
            >
              List
            </button>
          </div>
        </div>
      )}

      {listOpen ? (
        <ListSheet
          title={view.label}
          standings={view.standings}
          currency={view.currency}
          onOpenCategory={openCategory}
          onClose={() => setListOpen(false)}
        />
      ) : null}

      {moment === "report" ? (
        <TideReportView
          report={tideReport(periodEnd.closed, periodEnd.before)}
          closedLabel={periodEnd.closed.label}
          beforeLabel={periodEnd.before.label}
          currentLabel={view.label}
          currency={view.currency}
          onStart={() => {
            markSeen("report", closedKey);
            setMoment(null);
          }}
        />
      ) : null}
    </div>
  );
}
